// The live floor as the room is arranged (F-STN-19, spec/13 §3.4, §3.1): the saved plan — zones, fixtures and each
// placed station on its square — with every station's live state. Read-only; arranging is `FloorArrangePage`.
// When a manager saves a plan anywhere, `floor.plan.updated` re-reads it here (`realtime.ts`), so this view follows
// without a reload. Stations nobody has placed are listed under the room so none is ever missing from the floor.

import type { Zone } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Button, cx, EmptyState, SegmentedControl } from '@clavian/ui-web';
import { useMemo, useState, type CSSProperties, type ReactNode } from 'react';

import { NavIcon } from '../shell/nav-icons';
import { tokenColor } from '../stations/fields';
import type { FloorState, FloorStationView } from './live';
import { useFloorPlan } from './queries';
import { pillOf, stationAria } from './tile';

import './arrange.css';
import { useProblem } from '../api/use-problem';

const LIVE: ReadonlySet<FloorState> = new Set(['session', 'ending', 'locked']);
const ATTENTION: ReadonlySet<FloorState> = new Set(['overdue']);
const DIM: ReadonlySet<FloorState> = new Set(['offline', 'disabled']);

function place(rect: { readonly x: number; readonly y: number; readonly w: number; readonly h: number }): CSSProperties {
  return { gridColumn: `${rect.x + 1} / span ${rect.w}`, gridRow: `${rect.y + 1} / span ${rect.h}` };
}

export function FloorRoomView({
  organizationId,
  venueId,
  views,
  zones,
  called,
  onOpen,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly views: readonly FloorStationView[];
  readonly zones: readonly Zone[];
  readonly called: ReadonlySet<string>;
  readonly onOpen: (stationId: string) => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const [floor, setFloor] = useState(0);
  const plan = useFloorPlan(organizationId, venueId, floor);
  const byStation = useMemo(() => new Map(views.map((view) => [view.station.stationId, view])), [views]);
  const zoneById = useMemo(() => new Map(zones.map((zone) => [zone.id, zone])), [zones]);

  if (plan.data === undefined) {
    return plan.isError ? (
      <EmptyState size="compact" icon={<NavIcon name="floor" />} title={problem.text(plan.error, { fallback: t('floor.room.unavailable') })} action={<Button size="sm" onClick={() => void plan.refetch()}>{t('errors.retry')}</Button>} />
    ) : (
      <p className="console-subtle">{t('app.loading')}</p>
    );
  }

  const data = plan.data;
  const placed = new Set(data.stations.map((station) => station.stationId));
  const elsewhere = new Set(data.stationsElsewhere.map((entry) => entry.stationId));
  const unplaced = views.filter((view) => !placed.has(view.station.stationId) && !elsewhere.has(view.station.stationId));
  const floors = data.floorsInUse.length > 1 ? data.floorsInUse : null;

  return (
    <section className="console-room" aria-label={t('floor.room.label')}>
      {floors !== null ? (
        <SegmentedControl<string>
          size="sm"
          label={t('floor.arrange.floorLabel')}
          value={String(floor)}
          onValueChange={(value) => setFloor(Number(value))}
          options={floors.map((level) => ({ value: String(level), label: level === 0 ? t('floor.arrange.ground') : t('floor.arrange.level', { level }) }))}
        />
      ) : null}
      {data.stations.length === 0 && data.version === 0 ? (
        <EmptyState size="compact" icon={<NavIcon name="floor" />} title={t('floor.room.emptyTitle')} description={t('floor.room.empty')} />
      ) : (
        <div className="console-room__grid" style={{ '--room-columns': data.columns, '--room-rows': data.rows } as CSSProperties}>
          {data.zones.map((zone) => {
            const known = zoneById.get(zone.zoneId);
            return (
              <span
                key={`z-${zone.zoneId}`}
                className="console-room__zone"
                style={{ ...place(zone), '--zone-color': known === undefined ? undefined : tokenColor(known.colorToken) } as CSSProperties}
                title={known?.name}
                aria-hidden="true"
              />
            );
          })}
          {data.fixtures.map((fixture, index) => (
            <span key={`f-${index}`} className="console-room__fixture" style={place(fixture)} aria-hidden="true">
              {fixture.label ?? t(`floor.fixture.${fixture.kind}`)}
            </span>
          ))}
          {data.stations.map((station) => {
            const view = byStation.get(station.stationId);
            if (view === undefined) return null;
            const pill = pillOf(view, t);
            // The player on it, where known (spec/13 §3.1), as the tile names them.
            const aria = stationAria(view, t, null);
            return (
              <button
                key={`s-${station.stationId}`}
                type="button"
                className={cx(
                  'console-room__station',
                  LIVE.has(view.state) && 'console-room__station--live',
                  (ATTENTION.has(view.state) || called.has(station.stationId)) && 'console-room__station--attention',
                  DIM.has(view.state) && 'console-room__station--dim',
                )}
                style={place(station)}
                aria-label={called.has(station.stationId) ? t('floor.tile.ariaCalled', { tile: aria }) : aria}
                title={view.playerName === null ? pill.words : t('floor.tile.ariaWith', { tile: pill.words, detail: view.playerName })}
                onClick={() => onOpen(station.stationId)}
                data-station-tile={station.stationId}
              >
                {view.station.number}
              </button>
            );
          })}
        </div>
      )}
      {unplaced.length > 0 ? <p className="console-subtle">{t('floor.room.unplaced', { count: unplaced.length, numbers: unplaced.map((view) => view.station.number).join(', ') })}</p> : null}
    </section>
  );
}
